import {
  DatiAggiuntiviSide,
  LuogoCelebrazione,
  ModelEventoDTO,
  SubjectAnpr,
} from 'shell/models/act-category-list';
import { DateTime } from 'luxon';
import { defaultChoicePickerState } from 'shell/components/shared/choice-date/constants';
import { ActType } from 'shell/constants/act-type';
import { CODE_STATE_ITALIAN } from 'shell/constants/code-state-italian';
import { CivilUnionVM, ItalianVM } from 'mutation/view-models/civil-union';
import { AccordionPersonForm } from 'shell/view-models/accordion-person-form';
import { AccordionGeneralityForm } from 'shell/view-models/accordion-generality-form';

const transformPerson = (subjectAnpr: SubjectAnpr): AccordionPersonForm => {
  return {
    fiscalCode: subjectAnpr.codiceFiscale || '',
    surname: subjectAnpr.cognome || '',
    name: subjectAnpr.nome || '',
  };
};

const transformGenerality = (subjectAnpr: SubjectAnpr): AccordionGeneralityForm => {
  const isForeign = subjectAnpr.idStatoNascita !== CODE_STATE_ITALIAN;

  return {
    gender: subjectAnpr.sesso || '',
    birthDate: subjectAnpr.dataNascita ? DateTime.fromISO(subjectAnpr.dataNascita) : null,
    birthChoiceDate: subjectAnpr.dataNascita ? defaultChoicePickerState : undefined,
    aire: subjectAnpr.idComuneAIRE ? 'S' : 'N',
    isForeign,
    italian: isForeign
      ? undefined
      : {
          municipality: {
            description: subjectAnpr.nomeComuneNascita || '',
            province: subjectAnpr.siglaProvinciaNascita || '',
            code: subjectAnpr.idComuneNascita
              ? Number.parseInt(subjectAnpr.idComuneNascita.toString(), 10)
              : undefined,
            id: subjectAnpr.idComuneNascita || '',
            idProvince: subjectAnpr.idProvinciaNascita
              ? Number.parseInt(subjectAnpr.idProvinciaNascita.toString(), 10)
              : undefined,
            provinceCode: subjectAnpr.idProvinciaNascita || '',
          },
        },
    foreign: isForeign
      ? {
          state: {
            id: subjectAnpr.idStatoNascita || '',
            value: subjectAnpr.nomeStatoNascita || '',
          },
          municipality: {
            description: subjectAnpr.nomeComuneNascita || '',
            province: subjectAnpr.siglaProvinciaNascita || subjectAnpr.localitaEsteraNascita || '',
            id: subjectAnpr.idComuneNascita || '',
            idProvince: subjectAnpr.idProvinciaNascita
              ? Number.parseInt(subjectAnpr.idProvinciaNascita.toString(), 10)
              : undefined,
            provinceCode: subjectAnpr.idProvinciaNascita || '',
          },
        }
      : undefined,
    anprIdentifier: subjectAnpr.idsoggettoanpr,
  };
};

const transformCivilUnionItalian = (
  isForeign?: boolean,
  luogoCelebrazioneTranscod?: LuogoCelebrazione,
  datiAggiuntiviSide?: DatiAggiuntiviSide,
  isCasaComunale?: boolean
): ItalianVM => {
  if (isForeign) {
    return { municipality: null };
  }

  if (isCasaComunale && !Boolean(luogoCelebrazioneTranscod?.idComune)) {
    return {
      municipality: {
        id: datiAggiuntiviSide?.idComuneRegistrazione || undefined,
        description: datiAggiuntiviSide?.nomeComuneRegistrazione || '',
        province: datiAggiuntiviSide?.siglaProvinciaRegistrazione || '',
      },
    };
  }

  return {
    municipality: {
      description:
        luogoCelebrazioneTranscod?.nomeComune || datiAggiuntiviSide?.nomeComuneRegistrazione || '',
      province:
        luogoCelebrazioneTranscod?.siglaProvincia ||
        datiAggiuntiviSide?.siglaProvinciaRegistrazione ||
        '',
      code:
        luogoCelebrazioneTranscod?.idComune || datiAggiuntiviSide?.idComuneRegistrazione
          ? Number.parseInt(
              luogoCelebrazioneTranscod?.idComune ||
                datiAggiuntiviSide?.idComuneRegistrazione ||
                '',
              10
            )
          : undefined,
      id: luogoCelebrazioneTranscod?.idComune || datiAggiuntiviSide?.idComuneRegistrazione || '',
      idProvince:
        luogoCelebrazioneTranscod?.idProvincia || datiAggiuntiviSide?.idProvinciaRegistrazione
          ? Number.parseInt(
              luogoCelebrazioneTranscod?.idProvincia ||
                datiAggiuntiviSide?.idProvinciaRegistrazione ||
                '',
              10
            )
          : undefined,
      provinceCode:
        luogoCelebrazioneTranscod?.idProvincia ||
        datiAggiuntiviSide?.idProvinciaRegistrazione ||
        '',
    },
  };
};

export const transformCivilUnionAccordion = (
  subjectAnpr: SubjectAnpr,
  modelEventoDTO: ModelEventoDTO,
  datiAggiuntiviSide: DatiAggiuntiviSide
): CivilUnionVM => {
  const datiEventoUnioneCivile = modelEventoDTO.datiEventoUnioneCivile || {};

  const { luogoCelebrazioneTranscod } = datiAggiuntiviSide;
  const isForeign = luogoCelebrazioneTranscod
    ? luogoCelebrazioneTranscod.idStato !== CODE_STATE_ITALIAN
    : false;

  // se redatto in casa comunale non abbiamo il luogo celebrazione ma è di default Milano
  const isCasaComunale = modelEventoDTO.redattoNellaCasaComunale || false;

  const isWithSearchPerson = !subjectAnpr.idsoggettoanpr;

  return {
    searchPerson: isWithSearchPerson
      ? {
          fiscalCode: subjectAnpr.codiceFiscale || '',
          surname: subjectAnpr.cognome || '',
          name: subjectAnpr.nome || '',
          gender: subjectAnpr.sesso || '',
        }
      : undefined,
    idAnprSoggetto: subjectAnpr.idsoggettoanpr || undefined,
    person: transformPerson(subjectAnpr),
    generality: transformGenerality(subjectAnpr),
    dateEventChoice: datiEventoUnioneCivile.dataEvento ? defaultChoicePickerState : undefined,
    dateEvent: datiEventoUnioneCivile.dataEvento
      ? DateTime.fromISO(datiEventoUnioneCivile.dataEvento)
      : undefined,
    isForeign,
    italian: transformCivilUnionItalian(
      isForeign,
      luogoCelebrazioneTranscod,
      datiAggiuntiviSide,
      isCasaComunale
    ),
    foreign: isForeign
      ? {
          foreignState: luogoCelebrazioneTranscod?.idStato
            ? {
                id: luogoCelebrazioneTranscod?.idStato,
                value: luogoCelebrazioneTranscod.nomeStato || '',
              }
            : undefined,
          municipalityForeign: {
            description: luogoCelebrazioneTranscod?.nomeComune || '',
            province: luogoCelebrazioneTranscod?.siglaProvincia || '',
            id: luogoCelebrazioneTranscod?.idComune || '',
            idProvince: luogoCelebrazioneTranscod?.idProvincia
              ? Number.parseInt(luogoCelebrazioneTranscod.idProvincia.toString(), 10)
              : undefined,
            provinceCode: luogoCelebrazioneTranscod?.idProvincia || '',
          },
        }
      : { foreignState: null },
    actType: ActType.AttoDigitale,
    digital: {
      documentDate: modelEventoDTO.dataformazione
        ? DateTime.fromISO(modelEventoDTO.dataformazione)
        : null,
      istatCode: datiAggiuntiviSide?.idComuneRegistrazione || '',
      municipality: datiAggiuntiviSide?.idComuneRegistrazione
        ? {
            id: datiAggiuntiviSide?.idComuneRegistrazione || '',
            description: datiAggiuntiviSide?.nomeComuneRegistrazione || '',
            idProvince: datiAggiuntiviSide?.idProvinciaRegistrazione
              ? Number.parseInt(datiAggiuntiviSide?.idProvinciaRegistrazione, 10)
              : undefined,
            province: datiAggiuntiviSide?.siglaProvinciaRegistrazione || '',
          }
        : undefined,
      idAnsc: modelEventoDTO.idAnsc,
      year: modelEventoDTO.idAnsc ? modelEventoDTO.idAnsc.split('-')[0] : '',
      nationalNumber: modelEventoDTO.idAnsc ? modelEventoDTO.idAnsc.split('-')[1] : '',
      numberMunicipality: modelEventoDTO.idAnsc ? modelEventoDTO.idAnsc.split('-')[2] : '',
    },
  };
};
